import ProgressBar from "@ramonak/react-progress-bar";
import { useNavigate } from "react-router-dom";
import { openUpgradeModal } from "../../lib/upgradeModal";
import { BsJournalCheck, BsLightningChargeFill } from "react-icons/bs";
import { TrialSummary } from "../../types";
import { formatTrialTimeLeft } from "../../components/TrialBanner/TrialBanner";
import "./TrialPracticePanel.scss";

/**
 * Trial users can only quick-practise the whole subject: with a 50-question allowance,
 * per-module practice would only give a handful of questions per module.
 */
const TrialPracticePanel = ({
  trial,
  subjectId,
  ended,
}: {
  trial: TrialSummary;
  subjectId: string;
  ended: boolean;
}) => {
  const navigate = useNavigate();
  const remaining = Math.max(0, trial.limit - trial.used);
  const usedPercentage = trial.limit ? Math.min(100, (trial.used / trial.limit) * 100) : 0;

  const startTrialPractice = () => {
    navigate("/practice", {
      state: {
        assignmentName: "Quick Practice",
        practiceModuleIds: [],
        practiceSubjectId: subjectId,
        practiceSubjectName: trial.className,
      },
    });
  };

  const reviewAnswers = () => navigate(`/review?subjectId=${subjectId}`);

  if (ended) {
    return (
      <section className="trial-practice-panel">
        <span className="trial-practice-panel__badge">Trial ended</span>
        <h2>{trial.className}</h2>
        <p className="trial-practice-panel__summary">
          Your free trial has ended. You answered {trial.used} of {trial.limit} trial questions, and
          you can still look back over them and their explanations.
        </p>
        <ProgressBar
          completed={usedPercentage}
          bgColor="var(--brand-accent)"
          height="8px"
          isLabelVisible={false}
        />

        <div className="trial-practice-panel__actions">
          <button type="button" className="trial-practice-panel__start" onClick={openUpgradeModal}>
            Upgrade for full access
          </button>
          {trial.used > 0 && (
            <button type="button" className="trial-practice-panel__secondary" onClick={reviewAnswers}>
              <BsJournalCheck aria-hidden="true" /> Review my answers
            </button>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="trial-practice-panel">
      <span className="trial-practice-panel__badge">Free trial</span>
      <h2>{trial.className}</h2>
      <p className="trial-practice-panel__summary">
        You have used {trial.used} of {trial.limit} trial questions. Your trial{" "}
        {formatTrialTimeLeft(trial.expiresAt)}.
      </p>
      <ProgressBar
        completed={usedPercentage}
        bgColor="var(--brand-accent)"
        height="8px"
        isLabelVisible={false}
      />

      <div className="trial-practice-panel__actions">
        <button
          type="button"
          className="trial-practice-panel__start"
          onClick={startTrialPractice}
          disabled={remaining === 0}
        >
          <BsLightningChargeFill aria-hidden="true" /> Start quick practice (all modules)
        </button>
        {trial.used > 0 && (
          <button type="button" className="trial-practice-panel__secondary" onClick={reviewAnswers}>
            <BsJournalCheck aria-hidden="true" /> Review my answers
          </button>
        )}
      </div>

      <p className="trial-practice-panel__note">
        During your trial, quick practice draws questions from across the whole subject.
        Upgrade to practise by module, filter by question type, take timed exams, star
        questions and see detailed statistics.
      </p>
      <button type="button" className="trial-practice-panel__upgrade" onClick={openUpgradeModal}>
        Upgrade for full access
      </button>
    </section>
  );
};

export default TrialPracticePanel;
